import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { fetchAllTallies } from "@/lib/votes";

const EASE = [0.22, 1, 0.36, 1];
const SEG = ["bg-[#D4AF37]", "bg-[#38BDF8]", "bg-[#FF9F1C]"];

/* 0 = unanimous … 1 = perfect three-way tie */
export const splitScore = (counts) => {
  const total = counts.reduce((a, b) => a + b, 0);
  if (!total) return 0;
  return (1 - Math.max(...counts) / total) / (2 / 3);
};

export default function Leaderboard({ sites, onSelect, refreshKey }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let live = true;
    fetchAllTallies()
      .then((r) => live && (setRows(r), setErr(false)))
      .catch(() => live && setErr(true));
    return () => {
      live = false;
    };
  }, [refreshKey]);

  const ranked = useMemo(() => {
    if (!rows) return [];
    return rows
      .map((r) => ({ ...r, site: sites.find((s) => s.id === r.site_id), split: splitScore(r.counts) }))
      .filter((r) => r.site && r.total > 0)
      .sort((a, b) => b.split - a.split || b.total - a.total)
      .slice(0, 5);
  }, [rows, sites]);

  return (
    <motion.div
      data-testid="leaderboard"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, ease: EASE }}
      className="pointer-events-auto mt-14 border border-[#FF9F1C]/25 bg-[#12141C]/70 p-5 backdrop-blur sm:p-7"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <p className="font-mono text-[10px] tracking-[0.4em] text-[#FF9F1C]">MOST CONTESTED — CROWD SPLIT</p>
        <p className="font-mono text-[9px] tracking-[0.25em] text-white/35">
          <span className="mr-3"><i className="mr-1 inline-block h-1.5 w-1.5 bg-[#D4AF37] align-middle" />H.01</span>
          <span className="mr-3"><i className="mr-1 inline-block h-1.5 w-1.5 bg-[#38BDF8] align-middle" />H.02</span>
          <span><i className="mr-1 inline-block h-1.5 w-1.5 bg-[#FF9F1C] align-middle" />H.03</span>
        </p>
      </div>

      {err ? (
        <p data-testid="leaderboard-empty" className="mt-5 font-mono text-[9px] tracking-[0.25em] text-white/40">
          CROWD TALLY UNAVAILABLE
        </p>
      ) : rows && !ranked.length ? (
        <p data-testid="leaderboard-empty" className="mt-5 font-mono text-[9px] tracking-[0.25em] text-white/40">
          NO VOTES YET — OPEN A DOSSIER AND PICK THE HYPOTHESIS YOU FIND MOST CONVINCING
        </p>
      ) : (
        <ol className="mt-5 divide-y divide-white/10">
          {ranked.map((r, i) => (
            <li key={r.site_id}>
              <button
                data-testid={`leaderboard-row-${r.site_id}`}
                onClick={() => onSelect(r.site)}
                className="group grid w-full grid-cols-[2rem_1fr_auto] items-center gap-4 py-3 text-left sm:grid-cols-[2rem_minmax(0,260px)_1fr_auto]"
              >
                <span className="font-mono text-[10px] text-white/35">{String(i + 1).padStart(2, "0")}</span>
                <span className="truncate font-serif text-lg leading-tight text-[#F8FAFC] transition-colors group-hover:text-[#D4AF37]">
                  {r.site.name}
                </span>
                <span className="col-span-3 flex h-1.5 w-full overflow-hidden sm:col-span-1" aria-hidden>
                  {r.counts.map((c, k) => (
                    <span
                      key={k}
                      className={`${SEG[k]} transition-all duration-700`}
                      style={{ width: `${(c / r.total) * 100}%`, opacity: c ? 0.9 : 0 }}
                    />
                  ))}
                </span>
                <span
                  data-testid={`leaderboard-split-${r.site_id}`}
                  className="whitespace-nowrap font-mono text-[9px] tracking-[0.2em] text-white/50"
                >
                  SPLIT {Math.round(r.split * 100)}% · {r.total} VOTE{r.total === 1 ? "" : "S"}
                </span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </motion.div>
  );
}
